import { useState, useEffect } from 'react';
import { Modal, Select, Button, Stack, Group, Text } from '@mantine/core';
import { usePeople } from '@/hooks/api/usePeople';
import { useUpdateAsset, useAssignAsset, useUnassignAsset } from '@/hooks/api/useAssets';
import { useOrganization } from '@/hooks/useOrganization';
import { notifications } from '@mantine/notifications';
import type { Asset } from '@/types/models';

interface StatusChangeModalProps {
  opened: boolean;
  onClose: () => void;
  asset: Asset | null;
  onSuccess?: () => void;
}

type AssetStatus = 'Available' | 'Assigned' | 'Maintenance' | 'Retired' | 'Lost';

const STATUSES: { value: AssetStatus; label: string }[] = [
  { value: 'Available', label: 'Available' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Retired', label: 'Retired' },
  { value: 'Lost', label: 'Lost' },
];

export function StatusChangeModal({
  opened,
  onClose,
  asset,
  onSuccess,
}: StatusChangeModalProps) {
  const { organizationId } = useOrganization();
  const { data: people = [] } = usePeople(organizationId);
  const updateAsset = useUpdateAsset();
  const assignAsset = useAssignAsset();
  const unassignAsset = useUnassignAsset();

  const [status, setStatus] = useState<AssetStatus>('Available');
  const [personId, setPersonId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (opened && asset) {
      setStatus(asset.status as AssetStatus);
      setPersonId(null);
    }
  }, [opened, asset]);

  const handleClose = () => {
    setStatus('Available');
    setPersonId(null);
    onClose();
  };

  const handleSave = async () => {
    if (!asset || !organizationId) return;

    // Validate: if Assigned, must have a person
    if (status === 'Assigned' && !personId) {
      notifications.show({
        title: 'Error',
        message: 'Please select a person to assign the asset to',
        color: 'red',
      });
      return;
    }

    setIsLoading(true);
    try {
      // If changing TO Assigned status, assign the person
      if (status === 'Assigned' && personId) {
        // First unassign if currently assigned
        if (asset.assigned_to && asset.assigned_to.length > 0) {
          await unassignAsset.mutateAsync({
            assetId: asset.id,
            organizationId,
          });
        }
        // Assign to new person
        await assignAsset.mutateAsync({
          assetId: asset.id,
          personId: personId,
          organizationId,
        });
      } else if (status !== 'Assigned' && asset.status === 'Assigned') {
        // Changing FROM Assigned to something else - unassign
        if (asset.assigned_to && asset.assigned_to.length > 0) {
          await unassignAsset.mutateAsync({
            assetId: asset.id,
            organizationId,
            newStatus: status === 'Available' ? 'Available' : status as 'Maintenance' | 'Retired' | 'Lost',
          });
        } else {
          // No one assigned, just update status
          await updateAsset.mutateAsync({
            id: asset.id,
            data: { status },
            organizationId,
          });
        }
      } else {
        // Just update status
        await updateAsset.mutateAsync({
          id: asset.id,
          data: { status },
          organizationId,
        });
      }

      notifications.show({
        title: 'Status Updated',
        message: `Asset status changed to ${status}`,
        color: 'green',
      });

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update status',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const peopleOptions = people.map((person) => ({
    value: person.id,
    label: `${person.full_name}${person.email ? ` (${person.email})` : ''}`,
  }));

  if (!asset) return null;

  return (
    <Modal opened={opened} onClose={handleClose} title="Change Status" size="md">
      <Stack gap="md">
        <div>
          <Text size="sm" c="dimmed" mb="xs">
            Asset
          </Text>
          <Text fw={500}>
            {asset.tag_id} - {asset.description}
          </Text>
        </div>

        <Select
          label="Status"
          placeholder="Select status"
          data={STATUSES}
          value={status}
          onChange={(val) => {
            if (val) {
              setStatus(val as AssetStatus);
              // Clear person if not Assigned
              if (val !== 'Assigned') {
                setPersonId(null);
              }
            }
          }}
          required
        />

        {status === 'Assigned' && (
          <Select
            label="Assign to"
            placeholder="Select a person"
            data={peopleOptions}
            value={personId}
            onChange={setPersonId}
            searchable
            required
            error={status === 'Assigned' && !personId ? 'Required when assigning' : undefined}
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isLoading}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
